import React, { useEffect, useState } from "react";
import { Autocomplete ,createFilterOptions} from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import { useDispatch, useSelector } from "react-redux";
import {
  getPlaces,
  setPlaceData,
  setMultiSelectedPlaces,
} from "store/slice/places/placeSlice";
import MDBadge from "components/MDBadge";

const ALL_PLACES_OPTION = {
  title: "All places",
  parkingCode: "All Places",
  _id: "all",
  google: { formatted_address: "All Places" },
};

const MultiPlacePicker = (props) => {
  const { notDefaultPlace, isDashboard = false } = props;
  const dispatch = useDispatch();

  const rawPlaces = useSelector((state) => state.places.placeData).filter(
    (item) => item.status === 10
  );

   const placesData =
    notDefaultPlace && rawPlaces.length > 1
      ? [ALL_PLACES_OPTION, ...rawPlaces]
      : rawPlaces;

  const [selectedPlaceIds, setSelectedPlaceIds] = useState([]);
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        const response = await dispatch(getPlaces()).unwrap();
        if (response?.success) {
          const transformed = response.places
            .filter((item) => item.status === 10)
            .map((item) => ({
              ...item,
              title: item.google.formatted_address,
            }));
          dispatch(setPlaceData(transformed));
        }
      } catch (error) {
        console.error("Error getting places:", error);
      }
    };
    fetchPlaces();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

 useEffect(() => {
  if (initialized || placesData.length === 0) return;

  if (isDashboard) {
    const allOption = placesData.find((p) => p._id === "all");
    if (allOption) {
      setSelectedPlaceIds(["all"]);
      dispatch(setMultiSelectedPlaces([allOption]));
    } else {
      setSelectedPlaceIds([placesData[0]._id]);
      dispatch(setMultiSelectedPlaces([placesData[0]]));
    }
    setInitialized(true);
    return;
  }

  if (notDefaultPlace) {
    const allOption = placesData.find((p) => p._id === "all");
    if (allOption) {
      setSelectedPlaceIds(["all"]);
      dispatch(setMultiSelectedPlaces([allOption]));
      setInitialized(true);
      return;
    }
  }

  const storedId = localStorage.getItem("placeId");
  const firstRealPlace =
    placesData[0]?._id === "all" ? placesData[1] : placesData[0];
  const targetPlace =
    (storedId && placesData.find((p) => p._id === storedId)) || firstRealPlace;

  if (targetPlace) {
    setSelectedPlaceIds([targetPlace._id]);
    dispatch(setMultiSelectedPlaces([targetPlace]));
  }
  setInitialized(true);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [placesData.length, initialized]); 

  const handleOptionChange = (_, selectedOption, reason, detail) => {
    if (notDefaultPlace) {
      if (reason === "selectOption") {
        if (detail.option._id === "all") {
          setSelectedPlaceIds(["all"]);
          dispatch(setMultiSelectedPlaces([detail.option]));
        } else {
          const filtered = selectedOption.filter((o) => o._id !== "all");
          setSelectedPlaceIds(filtered.map((o) => o._id));
          dispatch(setMultiSelectedPlaces(filtered));
        }
      } else if (reason === "removeOption") {
        const remaining = selectedOption.filter(
          (o) => o._id !== detail.option._id
        );
        if (remaining.length === 0) {
          const allOption = placesData.find((p) => p._id === "all");
          if (allOption) {
            setSelectedPlaceIds(["all"]);
            dispatch(setMultiSelectedPlaces([allOption]));
          }
        } else {
          setSelectedPlaceIds(remaining.map((o) => o._id));
          dispatch(setMultiSelectedPlaces(remaining));
        }
      } else if (reason === "clear") {
        const allOption = placesData.find((p) => p._id === "all");
        if (allOption) {
          setSelectedPlaceIds(["all"]);
          dispatch(setMultiSelectedPlaces([allOption]));
        }
      }
    } else {
      if (selectedOption.length > 0) {
        setSelectedPlaceIds(selectedOption.map((o) => o._id));
        dispatch(setMultiSelectedPlaces(selectedOption));
      }
    }
  };

  const filterOptions = createFilterOptions({
      stringify: (option) =>
        [
          option?.parkingCode,
          option?.google?.formatted_address,
          option?.timeZoneId,
        ]
          .filter(Boolean)
          .join(" "),
    });

  return (
    <MDBox height="100%" mt={0.5} lineHeight={1}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={placesData}
        filterOptions={filterOptions}
        getOptionLabel={(option) => option.parkingCode ?? option}
        renderOption={(props, option) => (
          <li {...props}>
            <div>
              <MDBadge
                badgeContent={<p>{option.parkingCode.toString()}</p>}
                color="secondary"
                variant="gradient"
                size="md"
              />
              {" | "}
              {option?._id === "all"
                ? "All Places"
                : option?.google?.formatted_address}
            </div>
          </li>
        )}
        name="place"
        autoHighlight
        disableClearable
        size="small"
        value={
          placesData.filter((option) =>
            selectedPlaceIds.includes(option._id)
          ) || []
        }
        onChange={handleOptionChange}
        renderInput={(params) => (
          <MDInput
            label="Place"
            fullWidth
            inputProps={{
              ...params.inputProps,
              autoComplete: "new-password",
            }}
            {...params}
          />
        )}
      />
    </MDBox>
  );
};

export default MultiPlacePicker;
